import { type Order } from "@/api/order";
import { useOrderActions, type ActionBtn } from "@/hooks/useOrderActions";
import ProgressDots from "./ProgressDots";
import StatusPill from "./StatusPill";

const typeLabel: Record<string, { label: string; color: string; bg: string }> =
  {
    hotel: { label: "酒店", color: "#C2410C", bg: "#FFF4ED" },
    car: { label: "租车", color: "#92400E", bg: "#FFFBEB" },
    tour: { label: "线路", color: "#065F46", bg: "#ECFDF8" },
    guide: { label: "导游", color: "#5B21B6", bg: "#F5F3FF" },
  };

interface Props {
  order: Order;
  onDetail: () => void;
  // onSign: () => void;
  onPay?: () => void;
  onCancel?: () => void;
  onReview?: () => void;
  onInvoice?: () => void;
  onReorder?: () => void;
  onAfterSale?: () => void;
  onCancelRefund?: () => void;
  /** 是否达人订单（使用6步进度条） */
  isExpert?: boolean;
}

export default function SingleOrderCard({
  order,
  onDetail,
  // onSign,
  onPay,
  onCancel,
  onReview,
  onInvoice,
  onReorder,
  onAfterSale,
  onCancelRefund,
  isExpert = false,
}: Props) {
  const t = typeLabel[order.productType ?? "hotel"];

  const { getBtns } = useOrderActions({
    productType: order.productType ?? "hotel",
    mainStatusStep: order.mainStatusStep,
    mainStatus: order.mainStatus,
    hasRefund: order.hasRefund,
    refundStatus: order.refundStatus,
    hasComment: order.has_comment,
    hasInvoice: order.has_invoice,
  });

  const getActionBtns = (): ActionBtn[] => getBtns();
  const btnList = getActionBtns();
  const hasInvoice = Number(order.has_invoice) === 1;
  const invoiceStatus =
    order.invoice_status == null ? null : Number(order.invoice_status);
  const invoiceStatusText = hasInvoice
    ? invoiceStatus === 2
      ? "发票已开票"
      : "发票申请中"
    : "";

  const handleAction = (btn: ActionBtn, e: React.MouseEvent) => {
    e.stopPropagation();
    switch (btn.action) {
      case "pay":
        return onPay?.();
      case "cancel":
        return onCancel?.();
      case "review":
        return onReview?.();
      case "invoice":
        return onInvoice?.();
      case "afterSale":
        return onAfterSale?.();
      case "cancelRefund":
        return onCancelRefund?.();
      default:
        if (btn.type === "reorder") return onReorder?.();
        return onDetail();
    }
  };

  return (
    <div
      className="overflow-hidden cursor-pointer active:scale-[0.99] transition-all"
      style={{ background: "#fff", borderRadius: 20 }}
      onClick={onDetail}
    >
      <div
        className="px-4 pt-4 pb-3"
        style={{ borderBottom: "1px solid #F5F5F7" }}
      >
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex-1 min-w-0 mr-2">
            <div className="flex items-center gap-1.5">
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: t.bg, color: t.color }}
              >
                {t.label}
              </span>
              <span className="text-xs font-mono" style={{ color: "#B0B5BF" }}>
                #{order.orderNo ?? order.id.slice(-8)}
              </span>
            </div>
            <div className="text-xs mt-1" style={{ color: "#B0B5BF" }}>
              创建时间：{order.time} | 总金额：
              <span className="font-semibold" style={{ color: "#EF4444" }}>
                ¥{Number(order.total).toFixed(2)}
              </span>
            </div>
          </div>
          <StatusPill status={order.mainStatus} />
        </div>

        <ProgressDots step={order.mainStatusStep} type={order.productType} isExpert={isExpert} />
      </div>

      <div className="px-4 py-3 flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0">
          <img
            src={order.cover_url}
            alt={order.productName}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 line-clamp-1">
            {order.productName}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{order.remark}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {order.productDate}
          </div>
          {hasInvoice && (
            <div className="text-xs text-green-500 mt-1 mb-1">
              {invoiceStatusText}
            </div>
          )}
        </div>
      </div>

      <div
        className="px-4 py-3 flex flex-col gap-2"
        style={{ borderTop: "1px solid #F5F5F7" }}
      >
        <div className="flex items-baseline gap-1">
          <span className="text-xs text-gray-400">实付</span>
          <span className="font-bold text-lg" style={{ color: "#EF4444" }}>
            ¥{Number(order.total).toFixed(2)}
          </span>
        </div>
        <div className="flex justify-end gap-1">
          {btnList.map((btn, i) => (
            <button
              key={i}
              onClick={(e) => handleAction(btn, e)}
              className={`px-3.5 py-2 rounded-full text-xs cursor-pointer whitespace-nowrap font-medium ${
                btn.type === "primary" ? "font-semibold text-white" : ""
              }`}
              style={
                btn.type === "primary"
                  ? { background: "#FF6B35" }
                  : btn.type === "review"
                    ? { background: "#E0F2FE", color: "#0369A1" }
                    : btn.type === "service"
                      ? { background: "#F3E8FF", color: "#7C3AED" }
                      : btn.type === "reorder"
                        ? { background: "#ECFDF8", color: "#059669" }
                        : { background: "#F4F5F9", color: "#6B7280" }
              }
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
